import React, { useEffect, useState, useRef, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencil,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast.jsx";
import LoadingSpinner from "../../components/LoadingSpinner.jsx";
import { PermissionWrapper, hasPermission } from "../../helpers/permission.js";

const ManagePagesSeo = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFiltering, setIsFiltering] = useState(false);
  const navigate = useNavigate();
  const toastShownRef = useRef(false); // Prevent duplicate toast

  // Search filter states
  const [pageNameFilter, setPageNameFilter] = useState("");
  const [titleFilter, setTitleFilter] = useState("");
  const [descriptionFilter, setDescriptionFilter] = useState("");

  // Pagination states
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;

  // Fetch pages from API
  useEffect(() => {
    const fetchPages = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        const response = await axios.get(`${API_URL}pages`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.data && response.data.pages) {
          setPages(response.data.pages);
          setTotal(response.data.pages.length);
          setPageCount(Math.ceil(response.data.pages.length / limit));
          if (!toastShownRef.current) {
            showToast.success("Pages fetched successfully.", "success");
            toastShownRef.current = true;
          }
        }
      } catch (error) {
        if (!toastShownRef.current) {
          showToast.error("Failed to fetch pages.", "error");
          toastShownRef.current = true;
        }
        console.error("Error fetching pages:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPages();
  }, []);

  // Filter pages based on search criteria
  const filteredPages = useMemo(() => {
    return pages.filter((page) => {
      const pageName = page.page_name ?? "";
      const seoTitle = page.seo_title ?? "";
      const seoDesc = page.seo_desc ?? "";

      // Page Name filter
      if (
        pageNameFilter &&
        !pageName.toLowerCase().includes(pageNameFilter.toLowerCase())
      ) {
        return false;
      }

      // Title filter
      if (
        titleFilter &&
        !seoTitle.toLowerCase().includes(titleFilter.toLowerCase())
      ) {
        return false;
      }

      // Description filter
      if (
        descriptionFilter &&
        !seoDesc.toLowerCase().includes(descriptionFilter.toLowerCase())
      ) {
        return false;
      }

      return true;
    });
  }, [pages, pageNameFilter, titleFilter, descriptionFilter]);


  // Update pagination based on filtered results
  useEffect(() => {
    setTotal(filteredPages.length);
    setPageCount(Math.ceil(filteredPages.length / limit));
    setPage(1); // Reset to first page when filters change
  }, [filteredPages, limit]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  useEffect(() => {
    if (!loading) {
      setIsFiltering(true);
      const timer = setTimeout(() => {
        setIsFiltering(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [pageNameFilter, titleFilter, descriptionFilter]);


  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const paginatedPages = filteredPages.slice(start - 1, end);

  const handleEdit = (id) => {
    navigate(`/Seo/Pages/edit/${id}`);
  };

  // if (loading) {
  //   return <div className="page-container page-header-fixed page-sidebar-fixed fade in mt-5">Loading...</div>;
  // }


  useEffect(() => {
    const handleClearFilters = () => {
      setPageNameFilter("");
      setTitleFilter("");
      setDescriptionFilter("");
    };
    window.addEventListener('clearPageFilterEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearPageFilterEvent', handleClearFilters);
    };
  }, []);


  return (
    <div className="page-container page-header-fixed page-sidebar-fixed fade in mt-5">
      {/* {loading && <LoadingSpinner />} */}
      <div className="">
        <div className="panel panel-default with-rounded-corner with-shadow p-10">
          <div className="panel-heading border-0">
            <div className="row">
              <div className="col-md-6">
                <h4 className="panel-title pull-left">Manage Pages SEO</h4>
              </div>
              <div className="col-md-6" style={{ textAlign: "right" }}>
                {/* Pagination Controls */}
                {pages.length > 0 && (
                  <div className="d-inline-block">
                    <span style={{ fontSize: "0.8rem" }} className="me-2">
                      {start} to {end} of {total}
                    </span>
                    <button
                      className="btn btn-default btn-sm"
                      title="Previous"
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1}
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    &nbsp;
                    <button
                      className="btn btn-default btn-sm"
                      title="Next"
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === pageCount}
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {loading || pages.length > 0 ? (
            <div className="table-responsive user-table-container">
              <style>
                {`
                  .user-table-container {
                    position: relative;
                    max-height: 75vh;
                    overflow-y: auto;
                    overflow-x: auto;
                   }

                  /* First header row (titles) */
                  .user-table-container table thead tr:nth-child(1) th {
                    position: sticky;
                    top: 0;
                    z-index: 30;
                    background: #f8f9fa;
                  }

                 /* Second header row (search inputs) */
                 .user-table-container table thead tr:nth-child(2) th {
                   position: sticky;
                   top: 25px; /* adjust based on header height */
                   z-index: 25;
                   background: #fff;
                  }
               `}
              </style>
              <table className="table table-condensed table-bordered bg-white">
                <thead>
                  <tr>
                    <th style={{ textAlign: "center" }}>#</th>
                    <th>Page Name</th>
                    <th>Title</th>
                    <th>Description</th>
                    <th style={{ width: "1%" }}>Edit</th>
                  </tr>
                  <tr>
                    <th style={{ textAlign: "center" }}></th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search page..."
                        value={pageNameFilter}
                        onChange={(e) => setPageNameFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search title..."
                        value={titleFilter}
                        onChange={(e) => setTitleFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search description..."
                        value={descriptionFilter}
                        onChange={(e) => setDescriptionFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <button
                        className="btn btn-default btn-sm"
                        onClick={() => {
                          setPageNameFilter("");
                          setTitleFilter("");
                          setDescriptionFilter("");
                        }}
                        title="Clear filters"
                      >
                        Clear
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading || isFiltering ? (
                    Array.from({ length: 15 }).map((_, index) => (
                      <tr key={index}>
                        <td className="text-center">
                          <div className="skeleton-box" />
                        </td>
                        <td>
                          <div className="skeleton-box" />
                        </td>
                        <td>
                          <div className="skeleton-box" />
                        </td>
                        <td>
                          <div className="skeleton-box" />
                        </td>
                        <td className="text-center">
                          <div className="skeleton-box" style={{ width: "30px" }} />
                        </td>
                      </tr>
                    ))
                  ) : paginatedPages.length > 0 ? (
                    paginatedPages.map((page, index) => (
                      <tr key={page.id}>
                        <td
                          style={{
                            textAlign: "center",
                            verticalAlign: "middle",
                          }}
                        >
                          {index + 1}
                        </td>
                        <td>{page.page_name}</td>
                        <td
                          style={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            verticalAlign: "middle",
                          }}
                        >
                          {page.seo_title}
                        </td>
                        <td
                          style={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            verticalAlign: "middle",
                          }}
                        >
                          {page.seo_desc}
                        </td>
                        <td
                          style={{
                            textAlign: "center",
                            verticalAlign: "middle",
                          }}
                        >
                          <PermissionWrapper module="seo_pages" action="edit">
                            <button
                              className="btn btn-warning btn-xs"
                              onClick={() => handleEdit(page.id)}
                              title="Edit Page"
                            >
                              <FontAwesomeIcon icon={faPencil} />
                            </button>
                          </PermissionWrapper>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">
                        No pages match your search criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-3">No records found.</div>
          )}
        </div>
      </div>

      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
};

export default ManagePagesSeo;
